import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useUserContext } from '../../../context/UserContext';
import { GET_CARD } from '../../PersonalArea/PersonalAreaClub/queriesClub';
import Prices from '../../BaseCurrencyPrices/Prices';
import './AddProductInput.scss';

const countDecimals = (number) => {
    const numberString = number?.toString() || '0';
    const dotIndex = numberString.indexOf('.');
    return dotIndex !== -1 ? numberString.slice(dotIndex + 1).length : 0;
};

const AddProductInput = ({ minSaleQty, quantity, setQuantity, advancedInventory, prices }) => {
    const decimalPlaces = countDecimals(minSaleQty);
    const { user } = useUserContext();

    const { data: card, loading: cardLoading } = useQuery(GET_CARD, {
        fetchPolicy: 'no-cache',
        skip: !user,
    });

    const enableQtyIncrements = advancedInventory?.enable_qty_increments === '1';
    const qtyIncrements = Number(advancedInventory?.qty_increments) || minSaleQty || 1;
    const unit = advancedInventory?.unit?.replace(/^\//, '') || '';

    const updateQuantity = (qty) => {
        setQuantity(parseFloat(qty.toFixed(decimalPlaces)));
    };

    // Obliczanie najlepszej ceny dla trybu dziesiętnego
    let bestPrice = 0;
    if (enableQtyIncrements && prices?.length > 0) {
        const allPrices = prices
            .map((price) => {
                if (
                    price.price_type === 'simple_loyalty' &&
                    card?.historyCustomerLoyaltyCard?.loyalty?.[0]?.isActive
                ) {
                    return Number(price.amount);
                }
                return price.price_type !== 'simple_loyalty' ? Number(price.amount) : null;
            })
            .filter((price) => price !== null);
        bestPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
    }

    const packageQuantity = quantity >= minSaleQty ? parseInt((quantity / minSaleQty).toFixed(0)) : 1;
    const totalQuantityAmount = enableQtyIncrements && bestPrice ? (bestPrice * quantity).toFixed(2) : 0;

    const decrement = () => {
        if (enableQtyIncrements) {
            if (packageQuantity > 1) {
                updateQuantity((packageQuantity - 1) * minSaleQty);
            }
        } else if (quantity > 1) {
            updateQuantity(quantity - 1);
        }
    };

    const increment = () => {
        if (enableQtyIncrements) {
            updateQuantity((packageQuantity + 1) * minSaleQty);
        } else {
            updateQuantity(quantity + 1);
        }
    };

    if (cardLoading) return null; // Opcjonalne: można dodać loader

    return (
        <>
            {!enableQtyIncrements && (
                <div className="sidebar-add-product-quantity">
                    <button
                        className="sidebar-add-product-quantity__button sidebar-add-product-quantity__button_dec"
                        onClick={decrement}
                        disabled={quantity <= 1}
                    >
                        -
                    </button>
                    <input
                        type="number"
                        className="sidebar-add-product-quantity__input"
                        value={quantity}
                        disabled
                    />
                    <button
                        className="sidebar-add-product-quantity__button sidebar-add-product-quantity__button_inc"
                        onClick={increment}
                    >
                        +
                    </button>
                </div>
            )}
            {enableQtyIncrements && (
                <div className="sidebar-add-product-quantity-decimal">
                    <div style={{ display: 'flex' }}>
                        <input
                            type="number"
                            className="sidebar-add-product-quantity-decimal__inputDecimalQuantity"
                            value={quantity}
                            disabled
                        />
                        {unit && (
                            <input
                                type="text"
                                className="sidebar-add-product-quantity-decimal__inputUnit"
                                value={unit}
                                disabled
                            />
                        )}
                        <p className="sidebar-add-product-quantity-decimal__equally">=</p>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <button
                            className="sidebar-add-product-quantity-decimal__button sidebar-add-product-quantity-decimal__button_dec"
                            onClick={decrement}
                            disabled={packageQuantity <= 1}
                        >
                            -
                        </button>
                        <input
                            type="number"
                            className="sidebar-add-product-quantity-decimal__input"
                            value={packageQuantity}
                            disabled
                        />
                        <input
                            type="text"
                            className="sidebar-add-product-quantity-decimal__inputPackage"
                            value="paket"
                            disabled
                        />
                        <button
                            className="sidebar-add-product-quantity-decimal__button sidebar-add-product-quantity-decimal__button_inc"
                            onClick={increment}
                        >
                            +
                        </button>
                        {bestPrice > 0 && (
                            <>
                                <p className="sidebar-add-product-quantity-decimal__equally">=</p>
                                <div className="sidebar-add-product-quantity-decimal__totalPriceDesktop">
                                    <p className="sidebar-add-product-quantity-decimal__totalPrice">
                                        <Prices amount={totalQuantityAmount} />
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                    {bestPrice > 0 && (
                        <div className="sidebar-add-product-quantity-decimal__totalPriceMobile">
                            <p className="sidebar-add-product-quantity-decimal__totalPrice">
                                <Prices amount={totalQuantityAmount} />
                            </p>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

AddProductInput.propTypes = {
    minSaleQty: PropTypes.number,
    quantity: PropTypes.number.isRequired,
    setQuantity: PropTypes.func.isRequired,
    advancedInventory: PropTypes.shape({
        enable_qty_increments: PropTypes.string,
        qty_increments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit: PropTypes.string,
    }),
    prices: PropTypes.arrayOf(
        PropTypes.shape({
            price_type: PropTypes.string,
            amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
};

export default AddProductInput;